import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { LoginRequest, LoginResponse } from '../types/auth';
import type { ApartmentsDefaultsResponse } from '../types/apartments';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    const baseURL = process.env.REACT_APP_API_BASE_URL;

    if (!baseURL) {
      throw new Error('REACT_APP_API_BASE_URL is not set');
    }

    this.api = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });


    // Add response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Check if token was refreshed
        const newToken = response.headers['x-new-access-token'];
        if (newToken) {
          localStorage.setItem('accessToken', newToken);
          // Update the Authorization header for future requests
          this.api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        }
        return response;
      },
      (error) => {
        const status = error?.response?.status;
        if (status === 401) {
          // Clear auth and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          delete this.api.defaults.headers.common['Authorization'];
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.replace('/login');
          }
        }
        return Promise.reject(error);
      }
    );

    // Add request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await this.api.post<LoginResponse>('/api/auth/login', credentials);
      // Store the token
      localStorage.setItem('accessToken', response.data.access_token);
      // Set the Authorization header for future requests
      this.api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error && error.response && typeof error.response === 'object' && 'data' in error.response && error.response.data && typeof error.response.data === 'object' && 'error' in error.response.data) {
        throw new Error((error.response.data as { error: string }).error || 'Login failed');
      }
      throw new Error('Network error');
    }
  }

  async getApartments(): Promise<ApartmentsDefaultsResponse> {
    const response = await this.api.get<ApartmentsDefaultsResponse>('/api/pdf/apartments/defaults');
    return response.data;
  }

  async generateReceipt(settings: unknown): Promise<Blob> {
    const response = await this.api.post('/api/pdf/receipt', { settings }, { responseType: 'blob' });
    return response.data as Blob;
  }

  async generatePaymentNotice(settings: unknown): Promise<Blob> {
    const response = await this.api.post('/api/pdf/payment-notice', { settings }, { responseType: 'blob' });
    return response.data as Blob;
  }

  async generateChargeRegularization(settings: unknown): Promise<Blob> {
    const response = await this.api.post('/api/pdf/charge-regularization', { settings }, { responseType: 'blob' });
    return response.data as Blob;
  }

  async generateRentIncrease(settings: unknown): Promise<Blob> {
    const response = await this.api.post('/api/pdf/rent-increase', { settings }, { responseType: 'blob' });
    return response.data as Blob;
  }

  async logout(): Promise<void> {
    try {
      // Invalidate token on the server
      await this.api.post('/api/auth/logout');
    } catch {
      // Continue with local cleanup even if server request fails
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }
}

export const apiService = new ApiService();